import { useEffect, useState, type FormEvent } from 'react'
import { isPasskeyBridge } from '@reptree/shared'
import { useAuth } from '../lib/auth'
import { ENV } from '../lib/env'
import { finishConfirmation, passkeyEnv, passkeyProblem, prepareConfirmation, secondSteps, startCeremony, type PasskeyEnv } from '../lib/passkey'
import { usePreparedChallenge } from '../lib/usePreparedChallenge'
import { resyncSession } from '../lib/api'
import { Logo } from './Logo'
import { Button, Field, Input } from './ui'

/** The method that last got this device through, so the gate opens on it next time. */
const LAST_KEY = 'reptree-2fa-last'
const lastUsed = (): string | null => {
  try {
    return localStorage.getItem(LAST_KEY)
  } catch {
    return null
  }
}
const remember = (m: 'passkey' | 'code') => {
  try {
    localStorage.setItem(LAST_KEY, m)
  } catch {
    /* private mode */
  }
}

/**
 * Full-screen second step, shown when the account has a passkey or an authenticator app and this session has not
 * passed it yet. A passkey is offered first (one tap, then the device's fingerprint, face or screen lock); the
 * 6-digit authenticator code is the alternative when the account has both. The challenge is fetched ahead so the
 * tap goes straight to the device prompt, which keeps Safari happy. Nothing here names the browser's or Supabase's
 * own error text. Signing out from here signs out this device only.
 */
export function MfaGate() {
  const { session, completeMfa, finishMfa, signOutHere } = useAuth()
  const email = session?.user?.email ?? ''
  const [env, setEnv] = useState<PasskeyEnv | null>(null)
  const [methods, setMethods] = useState<{ passkey: boolean; app: boolean; stuck: boolean; appIds?: string[]; passkeyNames: string[] } | null>(null)
  const [failures, setFailures] = useState(0)
  const [mode, setMode] = useState<'passkey' | 'code'>('passkey')
  const [code, setCode] = useState('')
  const [hint, setHint] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Which second steps this account has (read again when a passkey turns out to be gone), and whether this browser can
  // use a passkey here.
  const [reads, setReads] = useState(0)
  useEffect(() => {
    let live = true
    void (async () => {
      const e = await passkeyEnv()
      let error = false
      let next: NonNullable<typeof methods>
      try {
        const steps = await secondSteps()
        next = {
          passkey: steps.passkeys.length > 0,
          app: steps.apps.length > 0,
          stuck: steps.stuck,
          appIds: steps.apps.map((a) => a.id),
          passkeyNames: steps.passkeys.map((p) => p.name),
        }
      } catch {
        // A failed read falls back to the factors this session already carries, never to "no second step". By name
        // only here: the passkey bridge stands for the account's passkeys, any other app factor is an authenticator app.
        error = true
        const verified = (session?.user?.factors ?? []).filter((f) => f.status === 'verified')
        next = {
          passkey: verified.some((f) => isPasskeyBridge(f)),
          app: verified.some((f) => f.factor_type === 'totp' && !isPasskeyBridge(f)),
          stuck: false,
          passkeyNames: [],
        }
      }
      if (!live) return
      setEnv(e)
      setMethods(next)
      // Open on a passkey where this device can use one, unless the authenticator app is what worked here last, or this
      // computer has no built-in authenticator (then the app code is simpler than a phone-and-QR hand-off).
      const preferPasskey = next.passkey && e.usable && (!next.app || (e.platform && lastUsed() !== 'code'))
      setMode(preferPasskey ? 'passkey' : next.app ? 'code' : 'passkey')
      // No second step left (support reset it, or it was removed on another device): this session's copy of the
      // account is stale. A resync clears the gate, or signs this device out if the session itself is gone.
      if (!error && !next.passkey && !next.app && !next.stuck) void resyncSession()
    })()
    return () => {
      live = false
    }
  }, [reads])
  /** The passkeys changed on the server since this screen read them: read them again (never a retry loop). */
  const reread = () => setReads((n) => n + 1)

  // Keep a challenge ready while the passkey option is showing.
  const { prepared, failed, renew, take, release, busy: prompting } = usePreparedChallenge(
    () => prepareConfirmation(),
    mode === 'passkey' && !!env?.usable && !!methods?.passkey,
    (e) => {
      // The hook retries on its own with a growing wait; nothing here asks for another round at once.
      const problem = passkeyProblem(e, ENV.PASSKEY_RP_ID)
      setErr(problem.message)
      if ((e as { details?: { code?: string } } | null)?.details?.code === 'no_passkey') reread()
      else if (problem.resync) void resyncSession()
    },
  )

  function usePasskey() {
    if (!env) return
    const p = take()
    if (!p) {
      if (!prompting()) setHint('One moment, then tap again.')
      return
    }
    setErr(null)
    setHint(null)
    setBusy(true)
    // No await before the prompt: the tap must still count as the reason for it.
    startCeremony(p, { platform: env.platform, email })
      .then((credential) => finishConfirmation(p, credential))
      .then(() => {
        remember('passkey')
        return finishMfa()
      })
      .catch((e: unknown) => {
        const problem = passkeyProblem(e, ENV.PASSKEY_RP_ID)
        setBusy(false)
        if (problem.silent) return
        setErr(problem.message)
        setFailures((n) => n + 1)
        if ((e as { details?: { code?: string } } | null)?.details?.code === 'no_passkey') reread()
        else if (problem.resync) void resyncSession().then(renew)
        else if (problem.rechallenge) renew()
        if (problem.offerOther && methods?.app) setMode('code')
      })
      .finally(release)
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      await completeMfa(code.trim(), methods?.appIds)
      remember('code')
    } catch {
      setErr('That code did not work. Check the newest code in your authenticator app and try again.')
      setBusy(false)
    }
  }

  const switchTo = (next: 'passkey' | 'code') => {
    setErr(null)
    setBusy(false)
    setMode(next)
  }

  const passkeyHere = !!methods?.passkey && !!env?.usable
  const stranded = !!methods && methods.passkey && !env?.usable && !methods.app

  return (
    <div className="grid min-h-full place-items-center px-6 py-16">
      <div className="page-enter w-full max-w-sm rounded-card border bg-[color:rgb(var(--bg-elev))] p-8 hairline">
        <Logo className="text-lg" />
        <h1 className="mt-6 font-display text-2xl">Confirm it is you</h1>

        {!methods || !env ? (
          <p className="mt-2 text-sm text-[color:rgb(var(--muted))]">One moment…</p>
        ) : methods.stuck ? (
          <p className="mt-2 text-sm">
            Your two-step sign-in needs our help to finish. Email us at{' '}
            <a className="select-all underline" href="mailto:hello@reptree.io">hello@reptree.io</a> from this account&apos;s email address and we will get
            you back in.
          </p>
        ) : stranded ? (
          <p className="mt-2 text-sm text-[color:rgb(var(--muted))]">
            Your account is protected with a passkey. To finish signing in, open{' '}
            <a className="underline" href={`https://${ENV.PASSKEY_RP_ID}`}>{ENV.PASSKEY_RP_ID}</a> on the phone or computer that has
            it.
          </p>
        ) : mode === 'passkey' && passkeyHere ? (
          <div className="mt-2">
            <p className="text-sm text-[color:rgb(var(--muted))]">
              {env.platform
                ? 'Use your passkey to finish signing in. Your device asks you to confirm the way you unlock it: your face, your fingerprint, your passcode or your PIN.'
                : 'Your passkey is on your phone. Tap the button, then scan the QR code with that phone and confirm there. This computer needs Bluetooth turned on.'}
            </p>
            <Button type="button" onClick={usePasskey} disabled={busy || !prepared} className="mt-5 min-h-11 w-full justify-center">
              {busy ? 'Waiting for your device…' : prepared ? 'Use my passkey' : failed ? 'Not ready' : 'Getting ready…'}
            </Button>
            {failed && !prepared && (
              <Button type="button" variant="ghost" onClick={renew} className="mt-2 min-h-11 w-full justify-center">
                Try again
              </Button>
            )}
            {hint && <p role="status" className="mt-3 text-sm">{hint}</p>}
            {err && <p role="alert" className="mt-3 text-sm text-clay">{err}</p>}
            {failures > 0 && (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-[color:rgb(var(--muted))]">
                {methods.passkeyNames.length > 0 && <li>Your passkey is saved on: {methods.passkeyNames.join(', ')}.</li>}
                <li>If a QR code appears, scan it with the phone that has your passkey.</li>
                <li>Opened this from an email or message app? Open {ENV.PASSKEY_RP_ID} in Safari or Chrome instead.</li>
              </ul>
            )}
            {methods.app && (
              <button type="button" onClick={() => switchTo('code')} className="label-mono mt-4 block underline text-[color:rgb(var(--muted))] hover:text-[color:rgb(var(--fg))]">
                Use my authenticator app instead
              </button>
            )}
          </div>
        ) : (
          <div className="mt-2">
            <p className="text-sm text-[color:rgb(var(--muted))]">Enter the 6-digit code from your authenticator app.</p>
            <form onSubmit={submitCode} className="mt-5 space-y-4">
              <Field label="Code">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  autoFocus
                  placeholder="123456"
                />
              </Field>
              {err && <p role="alert" className="text-sm text-clay">{err}</p>}
              <Button type="submit" disabled={busy || code.trim().length < 6} className="min-h-11 w-full justify-center">
                {busy ? '…' : 'Verify'}
              </Button>
            </form>
            {passkeyHere && (
              <button type="button" onClick={() => switchTo('passkey')} className="label-mono mt-4 block underline text-[color:rgb(var(--muted))] hover:text-[color:rgb(var(--fg))]">
                Use my passkey instead
              </button>
            )}
          </div>
        )}

        <p className="mt-6 text-xs text-[color:rgb(var(--muted))]">
          Lost your device, or see a passkey you never set up? Email us right away at{' '}
          <a className="select-all underline" href="mailto:hello@reptree.io">hello@reptree.io</a> and we will help you get back in.
        </p>
        <button
          type="button"
          onClick={() => void signOutHere()}
          className="label-mono mt-4 text-[color:rgb(var(--muted))] hover:text-[color:rgb(var(--fg))]"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
